import { useEffect, useId, useRef } from "react";
import { Platform, Pressable, Text, View, type ViewStyle } from "react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  interpolateColor,
} from "react-native-reanimated";
import { colors } from "../theme/tokens";
import { haptic } from "../haptics";
import { playSound } from "../sounds";
import { getInteractionDelta } from "../utils/interactionStyles";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

// Keep enabled toggles visually tied to the textured care surfaces rather than
// introducing another flat accent colour. This is also the background used by
// the Help a Friend card on the You screen.
const activeTrackTexture =
  require("@src/apps/moon/assets/images/care-hero-glow-gradient.png") as number;

/**
 * Animated toggle switch. Audit task 2.5.
 *
 * Promotes the moon-app-local `EmberToggle` (from YouScreen/components.tsx)
 * into a canonical primitive. Quick animated knob, interpolated track
 * colour between border and the shared textured active surface, haptic + sound
 * on every toggle.
 *
 * Use this instead of RN's `Switch` for any in-app toggle — the platform
 * Switch has inconsistent visuals between iOS and Android and no shared
 * sound / haptic plumbing.
 *
 * State coverage (Phase 1.0 matrix):
 *   - idle / on / off — track + knob animation
 *   - disabled        — opacity 0.5, presses ignored
 *   - error / focused / loading — N/A; wrap inside MoonFormField if needed.
 */
type Props = {
  value: boolean;
  onChange: (next: boolean) => void;
  /** Inactive track colour. Defaults to `colors.border`. */
  inactiveColor?: string;
  disabled?: boolean;
  testID?: string;
  /** Accessible name for the switch. The visible label is usually a sibling
   *  Text, so pass it here to give the switch a non-empty accessible name. */
  accessibilityLabel?: string;
  /** Optional extra context announced after the switch name. */
  accessibilityHint?: string;
  /** ID of visible explanatory text that describes the switch on web. */
  accessibilityDescriptionId?: string;
  /**
   * Stable analytics identifier for the setting this switch controls.
   * The new value is never captured — a toggle can be a clinical preference.
   */
  action?: string;
  style?: ViewStyle;
};

const TRACK_W = 44;
const TRACK_H = 24;
const KNOB = 20;
const TRAVEL = TRACK_W - KNOB - 4;
const TOGGLE_DURATION_MS = 160;
const FEEDBACK_DELAY_MS = 180;

export function MoonToggle({
  value,
  onChange,
  inactiveColor = colors.border,
  disabled,
  testID,
  accessibilityLabel,
  accessibilityHint,
  accessibilityDescriptionId,
  action,
  style,
}: Props) {
  const progress = useSharedValue(value ? 1 : 0);
  const generatedDescriptionId = useId().replace(/:/g, "");
  const hasInlineWebDescription =
    Platform.OS === "web" &&
    Boolean(accessibilityHint) &&
    !accessibilityDescriptionId;
  const resolvedDescriptionId =
    accessibilityDescriptionId ??
    (hasInlineWebDescription
      ? `moon-toggle-description-${generatedDescriptionId}`
      : undefined);
  // Keep the just-requested value locally as well as receiving it from the
  // controlled prop. That makes two quick taps reliably alternate even before
  // React has committed the first parent update.
  const requestedValue = useRef(value);
  const isUnavailable = Boolean(disabled);
  const capturePress = usePressCapture("MoonToggle");

  useEffect(() => {
    requestedValue.current = value;
    progress.value = withTiming(value ? 1 : 0, {
      duration: TOGGLE_DURATION_MS,
    });
  }, [value, progress]);

  const knobStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: progress.value * TRAVEL }],
  }));

  const trackStyle = useAnimatedStyle(() => ({
    backgroundColor: interpolateColor(
      progress.value,
      [0, 1],
      [inactiveColor, colors.deepBrown],
    ),
  }));

  const textureStyle = useAnimatedStyle(() => ({
    opacity: progress.value,
  }));

  return (
    <Pressable
      accessibilityRole="switch"
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={accessibilityHint}
      accessibilityState={{ checked: value, disabled: isUnavailable }}
      // Inert testability hook: react-native-web 0.21 no longer forwards
      // accessibilityState.checked/disabled to the DOM, so mirror them via the
      // aria-* props so the switch exposes role=switch + aria-checked for
      // deterministic E2E assertions (and correct a11y on web).
      aria-checked={value}
      aria-disabled={isUnavailable}
      // react-native-web forwards `aria-describedby`, but not
      // `aria-description`. Callers can connect the switch to visible helper
      // text without duplicating that text in its accessible name.
      aria-describedby={resolvedDescriptionId}
      onPress={() => {
        if (isUnavailable) return;
        capturePress(pressLabel(accessibilityLabel), action);
        const next = !requestedValue.current;
        requestedValue.current = next;
        progress.value = withTiming(next ? 1 : 0, {
          duration: TOGGLE_DURATION_MS,
        });
        onChange(next);

        // On web, first-use audio-context construction can take a noticeable
        // amount of time. Let React commit the selected state before feedback
        // work so a first tap never appears to be ignored.
        setTimeout(() => {
          haptic(next ? "selection" : "light");
          playSound(next ? "toggle-on" : "toggle-off");
        }, FEEDBACK_DELAY_MS);
      }}
      disabled={isUnavailable}
      testID={testID}
    >
      {({ hovered, focused }) => (
        <View
          style={[
            { opacity: disabled ? 0.5 : 1 },
            style,
            getInteractionDelta({ hovered, focused }, { hoverOpacity: 0.92 }),
          ]}
        >
          <Animated.View
            style={[
              {
                width: TRACK_W,
                height: TRACK_H,
                borderRadius: TRACK_H / 2,
                justifyContent: "center",
                paddingHorizontal: 2,
                overflow: "hidden",
                backgroundColor: value ? colors.deepBrown : inactiveColor,
              },
              trackStyle,
            ]}
          >
            <View
              pointerEvents="none"
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
              }}
            >
              <Animated.Image
                source={activeTrackTexture}
                resizeMode="cover"
                style={[{ width: "100%", height: "100%" }, textureStyle]}
              />
            </View>
            <Animated.View
              style={[
                {
                  width: KNOB,
                  height: KNOB,
                  borderRadius: KNOB / 2,
                  backgroundColor: "#FFFFFF",
                  transform: [{ translateX: value ? TRAVEL : 0 }],
                },
                knobStyle,
              ]}
            />
          </Animated.View>
          {hasInlineWebDescription ? (
            <Text
              nativeID={resolvedDescriptionId}
              style={{
                position: "absolute",
                width: 1,
                height: 1,
                opacity: 0,
                overflow: "hidden",
              }}
            >
              {accessibilityHint}
            </Text>
          ) : null}
        </View>
      )}
    </Pressable>
  );
}
